import React, {useState, useCallback, useEffect, useRef} from 'react';
import {APIProvider, Map, AdvancedMarker} from '@vis.gl/react-google-maps';
import {isCompTypeActive} from "../dynamicforms/dyanimcFormUtil.tsx";
import {SelectedComp} from "../dynamicforms/fields/configurablecompsselect/configurableCompsSelectTypes.tsx";
import {CompSelectType} from "../dynamicforms/DynamicFormTypes.ts";

interface GoogleMapMarkersProps {
    lat: number | null;
    lng: number | null;
    markers?: GoogleMapMarker[];
    getSelectedCompByMlsIdOrAddress: (address: string | undefined, mlsId: string | undefined) => SelectedComp | undefined;
    onMarkerClick?: (marker: GoogleMapMarker) => void;
}

export interface GoogleMapMarker {
    latitude: number;
    longitude: number;
    address: string;
    compType?: CompSelectType;
    mlsId?: string;
}

export const GoogleMapWithMarkers: React.FC<GoogleMapMarkersProps> = ({ lat, lng, markers, onMarkerClick, getSelectedCompByMlsIdOrAddress }) => {
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [markerPositions, setMarkerPositions] = useState<GoogleMapMarker[]>([]);
    const mapRef = useRef<google.maps.Map | null>(null);

    useEffect(() => {
        // Only update markers if the API is loaded and the markers prop actually exists/changes
        if (googleMapsLoaded && markers) {
            setMarkerPositions(markers);
            fitMapToMarkers(markers);
        } else if (googleMapsLoaded && !markers) {
            // Handle case where markers might become null/undefined after being set
            setMarkerPositions([]);
        }
    }, [markers, googleMapsLoaded]); // Add dependencies

    const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    // Use provided center or null check
    if (!lat || !lng) return null;
    const center = {lat, lng};

    const handleApiLoaded = useCallback(async () => {
        setGoogleMapsLoaded(true);
    }, []);

    const fitMapToMarkers = useCallback((markers: GoogleMapMarker[]): void => {
        if (!mapRef.current || !window.google) return;

        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(center);

        if (markers && markers.length > 0) {
            markers.forEach((marker) => {
                bounds.extend({lat: marker.latitude, lng: marker.longitude});
            });

            if (mapRef.current) {
                mapRef.current.fitBounds(bounds, 50);
            }
        } else {
            mapRef.current.setCenter(center);
            mapRef.current.setZoom(16);
        }
    }, [center]);

    const getMapOptions = useCallback(() => {
        if (!window.google) return {};

        return {
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            mapTypeControl: true,
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: window.google.maps.ControlPosition.TOP_RIGHT
            },
            zoomControl: true,
            zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER
            },
            scaleControl: true,
            streetViewControl: true,
            streetViewControlOptions: {
                position: window.google.maps.ControlPosition.LEFT_TOP
            },
            fullscreenControl: true,
            fullscreenControlOptions: {
                position: window.google.maps.ControlPosition.TOP_LEFT
            },
            rotateControl: true,
            rotateControlOptions: {
                position: window.google.maps.ControlPosition.LEFT_CENTER
            },
            clickableIcons: true,
            disableDefaultUI: false,
            keyboardShortcuts: true
        };
    }, []);

    // House icon component for selected markers
    const HouseIcon = ({color}: { color: string }) => (
        <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M3 12L12 3L21 12M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21V15C9 14.4477 9.44772 14 10 14H14C14.5523 14 15 14.4477 15 15V21M9 21H15"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="white"
            />
        </svg>
    );

    return (
        <>
            <div>
                <p>*The property characteristics and below images here are from a 3rd party source and have not been confirmed by Stewart Valuation Intelligence</p>
            </div>
            <div className="maps-container border border-slate-200 rounded-md overflow-hidden relative h-dvh w-full max-h-150">
                <APIProvider apiKey={API_KEY} onLoad={handleApiLoaded}>
                    {googleMapsLoaded ? (
                        <Map
                            defaultCenter={center}
                            defaultZoom={16}
                            mapId="map-with-satellite"
                            style={{width: '100%', height: '100%'}}
                            {...getMapOptions()}
                            onCameraChanged={(e) => {
                                if (e.map && !mapRef.current) {
                                    mapRef.current = e.map;
                                    if (markers && markers.length > 0) {
                                        fitMapToMarkers(markers);
                                    }
                                }
                            }}
                        >
                            {/* Marker for the subject property */}
                            <AdvancedMarker position={center} title="Subject Property">
                            </AdvancedMarker>

                            {markerPositions.map((pos, index) => {
                                const selected = getSelectedCompByMlsIdOrAddress(pos.address, undefined) != undefined;
                                const markerColor = isCompTypeActive(pos.compType) ? '#00c950' : '#4285F4';

                                return (
                                    <AdvancedMarker
                                        key={`${pos.latitude}-${pos.longitude}-${index}`} // More robust key
                                        position={{lat: pos.latitude, lng: pos.longitude}}
                                        title={`Comparable ${index + 1}: ${pos.address}`} // Add a title for hover info
                                        onClick={() => onMarkerClick?.(pos)}
                                        zIndex={selected ? 1000 : 1}
                                    >
                                        {selected ? (
                                            // House icon for selected markers
                                            <div style={{
                                                position: 'relative',
                                                display: 'inline-block',
                                                filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.3))',
                                            }}>
                                                <HouseIcon color={markerColor}/>
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    color: markerColor,
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    zIndex: 1,
                                                    textShadow: '0 0 3px white, 0 0 3px white, 0 0 3px white',
                                                    pointerEvents: 'none'
                                                }}>
                                                    {index + 1}
                                                </div>
                                            </div>

                                        ) : (
                                            // Teardrop shape for unselected markers
                                            <div style={{
                                                position: 'relative',
                                                display: 'inline-block',
                                            }}>
                                                <div style={{
                                                    backgroundColor: markerColor,
                                                    borderRadius: '50% 50% 50% 0',
                                                    width: '30px',
                                                    height: '30px',
                                                    transform: 'rotate(-45deg)',
                                                    boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                                                }}></div>
                                                <div style={{
                                                    color: 'white',
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                    zIndex: 1,
                                                    pointerEvents: 'none'
                                                }}>
                                                    {index + 1}
                                                </div>
                                            </div>
                                        )}
                                    </AdvancedMarker>
                                );
                            })}
                        </Map>
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#f0f0f0'
                        }}>
                            Loading map...
                        </div>
                    )}
                </APIProvider>
            </div>
        </>
    );
};